import React, { useCallback, useEffect, useState } from 'react';
import './reveal-animations.css';

/**
 * Patrón reutilizable: "skeleton mientras se espera, cascada al revelar".
 *
 * Úsalo en cualquier flujo donde el usuario dispara una consulta (validar un RUC,
 * buscar un cliente por DNI, etc.) y, al terminar, aparecen campos nuevos que antes
 * no existían en el formulario.
 *
 * No depende de cuántos campos sean ni de qué tipo — eso lo decides tú al armar
 * tu formulario. Este hook solo coordina el estado y el tiempo de aparición.
 *
 * @example
 * ```tsx
 * const reveal = useRevealOnLoad();
 *
 * // Al empezar la consulta:   reveal.start();
 * // Si la consulta funcionó:  reveal.finish();
 * // Si falló o no hay datos:  reveal.fail();
 * // Si el usuario cambia el valor ya validado: reveal.reset();
 *
 * {reveal.isLoading && (
 *   <>
 *     <RevealSkeleton className="h-16" />
 *     <RevealSkeleton className="h-16" />
 *   </>
 * )}
 *
 * {datos && (
 *   <>
 *     <RevealField show={reveal.isSuccess} delayMs={reveal.getDelay(0)}>
 *       <Input label="Razón Social" value={datos.razon} readOnly />
 *     </RevealField>
 *     <RevealField show={reveal.isSuccess} delayMs={reveal.getDelay(1)}>
 *       <Input label="Domicilio Fiscal" value={datos.domicilio} readOnly />
 *     </RevealField>
 *   </>
 * )}
 * ```
 */

export type RevealStatus = 'idle' | 'loading' | 'success';

export interface UseRevealOnLoadOptions {
  /**
   * Milisegundos de diferencia entre la aparición de un campo y el siguiente.
   * Default: 55. Súbelo para un efecto más marcado, bájalo para algo más sutil.
   */
  staggerMs?: number;
}

export function useRevealOnLoad(options: UseRevealOnLoadOptions = {}) {
  const { staggerMs = 55 } = options;
  const [status, setStatus] = useState<RevealStatus>('idle');

  /** Llamar justo antes de iniciar la consulta (muestra el skeleton). */
  const start = useCallback(() => setStatus('loading'), []);
  /** Llamar cuando la respuesta ya está lista (dispara la cascada). */
  const finish = useCallback(() => setStatus('success'), []);
  /** Llamar cuando la consulta falló o no hay nada que mostrar (quita el skeleton). */
  const fail = useCallback(() => setStatus('idle'), []);
  /** Llamar al editar el valor de origen (p. ej. el usuario cambia el RUC ya validado). */
  const reset = useCallback(() => setStatus('idle'), []);

  /** Retraso en ms para el campo en la posición `index`. Pásalo a <RevealField delayMs={...}>. */
  const getDelay = useCallback((index: number) => index * staggerMs, [staggerMs]);

  return {
    status,
    isIdle: status === 'idle',
    isLoading: status === 'loading',
    isSuccess: status === 'success',
    start,
    finish,
    fail,
    reset,
    getDelay,
  };
}

/* ------------------------------------------------------------------------ */

export interface RevealSkeletonProps {
  /** Alto y redondeo del placeholder — deben parecerse a los del campo real que reemplaza. Ej: 'h-10 rounded-lg'. */
  className?: string;
}

/**
 * Placeholder gris con pulso, del tamaño del campo que va a aparecer.
 * Ajusta `className` (el alto) para que calce con el campo real y no haya salto de layout.
 */
export function RevealSkeleton({ className = 'h-11 rounded-md' }: RevealSkeletonProps) {
  return <div className={`bg-surface-2 animate-pulse ${className}`} aria-hidden="true" />;
}

/* ------------------------------------------------------------------------ */

export interface RevealFieldProps {
  /** Controla si el campo debe estar visible (normalmente `reveal.isSuccess`). */
  show: boolean;
  /** Retraso en ms, normalmente `reveal.getDelay(index)`. */
  delayMs?: number;
  /** Si es true, el campo se ve de inmediato, sin animación (p. ej. al editar un registro existente). */
  inmediato?: boolean;
  children: React.ReactNode;
  className?: string;
}

/**
 * Envuelve un campo para que aparezca con fade + slide, en el momento y con el
 * retraso que le indiques. No le importa qué hay adentro: un input, un select,
 * un texto de solo lectura, etc.
 *
 * El campo se dibuja primero oculto y pasa a visible un instante después; así la
 * animación ocurre aunque el campo se monte en el mismo momento en que llegan los datos.
 */
export function RevealField({ show, delayMs = 0, inmediato = false, children, className = '' }: RevealFieldProps) {
  const [visible, setVisible] = useState(inmediato && show);

  useEffect(() => {
    if (inmediato) {
      setVisible(show);
      return;
    }
    if (!show) {
      setVisible(false);
      return;
    }
    let segundoFrame = 0;
    const primerFrame = requestAnimationFrame(() => {
      segundoFrame = requestAnimationFrame(() => setVisible(true));
    });
    return () => {
      cancelAnimationFrame(primerFrame);
      cancelAnimationFrame(segundoFrame);
    };
  }, [show, inmediato]);

  return (
    <div
      className={`reveal-field ${visible ? 'reveal-field--visible' : ''} ${className}`}
      style={{ transitionDelay: inmediato ? undefined : `${delayMs}ms` }}
    >
      {children}
    </div>
  );
}
